// Collection pages — /[lang]/<slug>, each listing the tours whose
// `tours.themes` contain its `theme` (the tours_by_theme RPC; the theme rules
// are rows of tour_theme_rules in supabase/sites/<site>.sql). The lists
// themselves are the site's (SITE_UI.collections, from its src/lib/collections.ts);
// this module holds their shape and the rules every site applies to them.
//
// Framework-free: read by generateStaticParams, the build-time prefetch and
// the screens alike. Copy (titles, intros) lives in the i18n resources under
// `collections.<slug>`.

import { SITE_UI } from '@site-ui';

import type { GuideKey } from './guides';

export type CollectionKind = 'category' | 'topic' | 'mood' | 'area';

export type Collection = {
  /** URL segment: /[lang]/<slug>. */
  slug: string;
  /** `tours.themes` value (a tour_theme_rules.theme). */
  theme: string;
  kind: CollectionKind;
  /** Line icon (24×24 viewBox path, stroked), for the category strip and the mood tiles. */
  icon: string;
  /** A topic's category: linked from its page, and its breadcrumb's middle crumb. */
  parent?: string;
  /**
   * The guides its "Read also" block links to, in order — each by its
   * language-neutral key, the English version's slug (GuideKey; resolved to
   * the page language's version by ./guides.ts).
   */
  guides?: readonly GuideKey[];
};

/**
 * Every page with a "Read also" block, by its path under the locale (a
 * collection's slug, or a hub's — SITE_UI.hubGuides), with its guides — what
 * the build seeds.
 */
export function pagesWithGuides(): { page: string; guides: readonly GuideKey[] }[] {
  return [
    ...SITE_UI.collections.flatMap((c) => (c.guides?.length ? [{ page: c.slug, guides: c.guides }] : [])),
    ...Object.entries(SITE_UI.hubGuides).map(([page, guides]) => ({ page, guides })),
  ];
}

export function collectionBySlug(slug: string | undefined): Collection | undefined {
  return SITE_UI.collections.find((c) => c.slug === slug);
}

export function collectionsOfKind(kind: CollectionKind): Collection[] {
  return SITE_UI.collections.filter((c) => c.kind === kind);
}

/** The topics of a category (`parent` = its slug), in list order. */
export function topicsOf(slug: string): Collection[] {
  return SITE_UI.collections.filter((c) => c.parent === slug);
}

/** Tours per collection page — the whole list; the site is one city. */
export const COLLECTION_TOURS_LIMIT = 100;

/**
 * When a collection page is indexable (and in sitemap.xml) — decided anew by
 * every build, as the catalog changes:
 *   1. it has copy of its own (`collections.<slug>.guide`): writing the guide
 *      is the decision that the page answers a search of its own;
 *   2. it lists at least one tour — a page of "tickets" with nothing to book
 *      sends the visitor straight back to the results;
 *   3. its list isn't a near copy of an indexable page before it (/boats
 *      first, then COLLECTIONS in order: categories, topics, moods, areas) —
 *      two pages with the same cards compete for one result. See
 *      findDuplicateLists.
 * /boats follows 1 and 2.
 */
export function isCollectionIndexable(hasGuide: boolean, tourCount: number, duplicateOf: string | undefined): boolean {
  return hasGuide && tourCount > 0 && !duplicateOf;
}

/**
 * Two lists are near copies from this similarity up (shared tours / tours of
 * either — Jaccard). Downtown with a rule had 15 tours, 14 of them the 14
 * Burj Khalifa tickets: 0.93. A topic inside its category stays below
 * (hot-air-balloon in scenic-flights: 0.65 on 2026-10-29 — were it most of
 * it, the topic page would rightly drop out), an area overlapping a category
 * far below (marina and dhow-cruise: under 0.5).
 */
export const DUPLICATE_LIST_SIMILARITY = 0.8;
/** From here up the build log lists a pair, to look at before it becomes one. */
export const SIMILAR_LIST_WARNING = 0.5;

/** Seed key of the build's verdict: slug → the earlier page its list copies. */
export const COLLECTION_DUPLICATES_SEED_KEY = 'collection-duplicates';

export type ListPage = { slug: string; hasGuide: boolean; ids: string[] };
export type SimilarPair = { slug: string; other: string; similarity: number };

function similarity(a: ReadonlySet<string>, b: ReadonlySet<string>): number {
  let shared = 0;
  for (const id of a) if (b.has(id)) shared++;
  const union = a.size + b.size - shared;
  return union ? shared / union : 0;
}

/**
 * Walks `pages` in order (the earlier page wins) and returns, for each page
 * whose list is a near copy of an earlier indexable one, that page's slug —
 * plus every pair from SIMILAR_LIST_WARNING up, for the build log. Pages
 * without a guide or without tours aren't indexable, so they never make a
 * later page a copy.
 */
export function findDuplicateLists(pages: ListPage[]): { duplicateOf: Record<string, string>; similar: SimilarPair[] } {
  const duplicateOf: Record<string, string> = {};
  const similar: SimilarPair[] = [];
  const kept: { slug: string; ids: Set<string> }[] = [];
  for (const page of pages) {
    if (!page.hasGuide || !page.ids.length) continue;
    const ids = new Set(page.ids);
    for (const other of kept) {
      const s = similarity(ids, other.ids);
      if (s >= SIMILAR_LIST_WARNING) similar.push({ slug: page.slug, other: other.slug, similarity: s });
      if (s >= DUPLICATE_LIST_SIMILARITY && !duplicateOf[page.slug]) duplicateOf[page.slug] = other.slug;
    }
    if (!duplicateOf[page.slug]) kept.push({ slug: page.slug, ids });
  }
  return { duplicateOf, similar };
}

/** Seed key of a collection page's tour list (the app's src/lib/prefetch.ts). */
export function collectionSeedKey(slug: string): string {
  return `collection:${slug}`;
}

/** One entry of the category strip: a collection, or a hub page (path, label key, icon). */
export type StripItem =
  | { kind: 'collection'; collection: Collection }
  | { kind: 'page'; path: string; labelKey: string; icon: string };


/**
 * The home page's category guide: per category collection, how many tours its
 * page lists and the top-ranked one's photo — computed by the build from the
 * collection lists, so the home page doesn't bake them.
 */
export type CategoryStat = { slug: string; count: number; image: string | null };
export const HOME_CATEGORIES_SEED_KEY = 'home-categories';
